import { FC, useContext } from "react";

import { API_REGION_STORAGE_KEY } from "@/api/region";
import { resolveApiRegion } from "@/components/online-reporting/citizen-engage/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

type ApiRegion = "ca" | "us";

const REGION_OPTIONS: { value: ApiRegion; label: string; flag: string }[] = [
  { value: "ca", label: "Canada", flag: "🇨🇦" },
  { value: "us", label: "United States", flag: "🇺🇸" },
];

/**
 * Points the dev/admin panel at either the Canadian ("ca") or US ("us") Rust
 * API so each region can be administered from the same login. Writes the
 * choice to `sessionStorage` — the session override that wins over the org's
 * and the user's configured region — then reloads, which is what applies it:
 * `api/index.ts` resolves the stored region at module load, and every React
 * Query cache refetches against the selected region's backend. Mirrors the org
 * switch in `AppBar`.
 */
export const DevRegionSwitcher: FC = () => {
  const { currentUser } = useContext(AuthStateContext);

  const activeRegion = resolveApiRegion({
    sessionApiRegion: sessionStorage.getItem(API_REGION_STORAGE_KEY),
    userApiRegion: currentUser?.api_region,
  }) as ApiRegion;

  const switchRegion = (region: ApiRegion) => {
    if (region === activeRegion) return;
    sessionStorage.setItem(API_REGION_STORAGE_KEY, region);
    window.location.reload();
  };

  return (
    <div className="dev-region-switcher" role="group" aria-label="API region">
      <span className="dev-region-switcher__label">API region</span>
      {REGION_OPTIONS.map((option) => {
        const isActive = option.value === activeRegion;
        return (
          <button
            key={option.value}
            type="button"
            className={
              "dev-region-switcher__option" + (isActive ? " is-active" : "")
            }
            aria-pressed={isActive}
            onClick={() => switchRegion(option.value)}
          >
            <span aria-hidden="true">{option.flag}</span> {option.label}
          </button>
        );
      })}
    </div>
  );
};
